import React from 'react';
import styled, { keyframes } from 'styled-components';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Icon, Menu, Dropdown } from 'antd';
import { redirectPath } from '../../config/config';

const shimmer = keyframes`
  0%   { background-position: -300% center; }
  100% { background-position: 300% center; }
`;

const beat = keyframes`
  0%, 100% { transform: scale(1); }
  50%       { transform: scale(1.18); }
`;

const StyledHeader = styled.header`
  position: fixed;
  top: 0;
  z-index: 10;
  width: 100%;
  margin: 0;
  padding: 0;
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  grid-area: header;
  background-color: #1a6080;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.18);

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, #1890ff, #13c2c2, #52c41a, #1890ff);
    background-size: 300% auto;
    animation: ${shimmer} 4s linear infinite;
  }
`;

const Brand = styled.div`
  display: flex;
  align-items: stretch;
  margin: 0;
  padding: 0;
`;

const BrandLink = styled(Link)`
  background: linear-gradient(135deg, #227093 0%, #1a5f7a 100%);
  width: ${props => (props.viewmenu ? '14em' : '6em')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  text-align: center;
  padding: 0 12px;
  margin: 0;
  text-decoration: none;
  text-transform: uppercase;
  min-height: 54px;
  transition: width 0.4s cubic-bezier(0.22, 1, 0.36, 1), background 0.2s;
  border-right: 1px solid rgba(255, 255, 255, 0.08);
  letter-spacing: 1.5px;

  &:hover {
    background: linear-gradient(135deg, #2980b9 0%, #227093 100%);
    color: #fff;
  }

  @media (max-width: 970px) {
    width: ${props => (props.viewmenu ? '6em' : '14em')};
  }
`;

const BrandName = styled.span`
  font-weight: 800;
  font-size: 13px;
  letter-spacing: 2px;
  color: #fff;
`;

const Toggle = styled.button`
  min-height: 54px;
  width: 48px;
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
  border: 0;
  border-right: 1px solid rgba(255, 255, 255, 0.08);
  cursor: pointer;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.14);
  }
  &:active {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const SideMenu = styled.nav`
  display: flex;
  justify-items: center;
  align-items: center;
  padding-right: 18px;
  gap: 8px;
  color: #fff;
`;

const UserBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 12px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.1);
  cursor: pointer;
  transition: background 0.2s;
  border: 1px solid rgba(255, 255, 255, 0.12);

  &:hover {
    background: rgba(255, 255, 255, 0.18);
  }
`;

const UserAvatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1890ff, #13c2c2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
  animation: ${beat} 3s ease-in-out infinite;
`;

const Username = styled.span`
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  color: #fff;
  letter-spacing: 0.5px;
`;

const Header = ({ username, setViewMenu, viewMenu, setLoggedOutCLicked }) => {
  const menu = (
    <Menu>
      <Menu.Item key="0" style={{ color: '#ff4d4f' }}>
        <span onClick={() => setLoggedOutCLicked(true)}>
          <Icon type="logout" style={{ marginRight: 8 }} />
          Log out
        </span>
      </Menu.Item>
    </Menu>
  );

  return (
    <StyledHeader>
      <Brand>
        <BrandLink viewmenu={+viewMenu} to={redirectPath}>
          <BrandName>{process.env.REACT_APP_PROJECT_NAME}</BrandName>
        </BrandLink>
        <Toggle type="button" onClick={() => setViewMenu(val => !val)}>
          <FontAwesomeIcon style={{ fontSize: 16 }} icon="bars" />
        </Toggle>
      </Brand>
      <SideMenu>
        <Dropdown overlay={menu} trigger={['click']} placement="bottomRight">
          <UserBadge>
            <UserAvatar>{(username || '?')[0].toUpperCase()}</UserAvatar>
            <Username>{username}</Username>
            <Icon type="down" style={{ fontSize: 10, opacity: 0.7 }} />
          </UserBadge>
        </Dropdown>
      </SideMenu>
    </StyledHeader>
  );
};

Header.propTypes = {
  username: PropTypes.string,
  viewMenu: PropTypes.bool.isRequired,
  setViewMenu: PropTypes.func.isRequired,
  setLoggedOutCLicked: PropTypes.func.isRequired,
};

Header.defaultProps = {
  username: '',
};

export default Header;